import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { CSP_NONCE, Inject, NgModule } from "@angular/core";
import { BrowserModule, Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { AUC_AF_CONFIG, AUC_APP_CONFIG, AUC_AUTH_CODE_FLOW_CONFIG, AUC_AUTH_LANDING_CONFIG, AUC_CONFIG_TOKEN, AUC_DATE_FORMATS, AUC_DEFAULT_CONFIG, AUC_ICONS_CONFIG_TOKEN, AUC_ICONS_DEFAULT_CONFIG, AUC_INPUT_DEFAULT_CONFIG, AUC_LINKS_CONFIG_TOKEN, AUC_LINKS_DEFAULT_CONFIG, AUC_LOADING_CONFIG, AUC_MODEL_VALUE_OUTPUT_FORMATTERS_CONFIG_DEFAULT, AUC_PORTALS_DEFAULT_CONFIG, AUC_TABLE_CONFIG, AUC_VIEW_MANAGER_CONFIG, AucConfig } from "@auction-ui/config";
import { AucEntryPointComponent } from "@auction-ui/entryPoint/entry.point.component";
import { AucEntryPointModule } from "@auction-ui/entryPoint/entry.point.module";
import { AUC_ENVIRONMENT } from "@auction-ui/environments/environment";
import { AucFooterModule } from "@auction-ui/footer/footer.module";
import { AucHomeModule } from "@auction-ui/home/home.module";
import { AUC_MODEL_CONFIG } from "@auction-ui/modelConfigs/model.config";
import { AucRootAuthnModule } from "@auction-ui/root/authn/root.authn.module";
import { AucRootUnAuthnModule } from "@auction-ui/root/unauthn/root.unauthn.module";
import { AucServiceModule } from "@auction-ui/services/services.module";
import { AucUserService } from "@auction-ui/services/user.service";
import { NGX_MAT_DATE_FORMATS } from "@ngxmc/datetime-picker";
import { AF_CONFIG_TOKEN } from "@pattonair/auction-framework";
import { MF_APP_CONFIG_TOKEN, MF_AUTH_CONFIG_TOKEN, MF_AUTH_LANDING_CONFIG_TOKEN, MF_AUTHENTICATED_URLS_EXCLUDE_TOKEN, MF_AUTHENTICATED_URLS_TOKEN, MF_FILTER_CONFIG_TOKEN, MF_FILTER_CONVERTER_SERVICE_TOKEN, MF_INPUT_CONFIG_TOKEN, MF_LOADING_CONFIG_TOKEN, MF_MODEL_CONFIGS_TOKEN, MF_MODEL_VALUE_OUTPUT_FORMATTERS_CONFIG_DEFAULT_TOKEN, MF_PORTALS_CONFIG_TOKEN, MF_PORTALS_FILTER_CONFIG, MF_TABLE_CONFIG_TOKEN, MF_VIEW_MANAGER_CONFIG_TOKEN, MF_VIEW_MANAGER_LOCAL_STORAGE_KEY, MfAuthAccessTokenInjectorInterceptor, MfAuthService, MfAuthXsrfTokenInjectorInterceptor, MfDayJsDateModule, MfFillRouterOutletModule, MfPortalsFilterConverterService, MfPortalsModule, MfSessionStorageService, MfViewManagerSessionStorageService, MfViewManagerStorageService } from "@pattonair/material-framework";
import { AuthConfig, OAuthModule, OAuthStorage } from "angular-oauth2-oidc";
import { JwksValidationHandler } from "angular-oauth2-oidc-jwks";
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from "ngx-google-analytics";
import { AucAuctionDetailModule } from "./auctionDetail/auction.detail.module";
import { AucAuctionListModule } from "./auctionList/auction.list.module";
import { AucBidListModule } from "./bidList/bid.list.module";
import { AucRoutingModule } from "./routing.module";
import { AucWonListModule } from "./wonList/won.list.module";

const nonce = (
  document.querySelector("meta[name=\"CSP_NONCE\"]") as HTMLMetaElement
)?.content;

@NgModule({
  imports: [
    AucAuctionDetailModule,
    AucAuctionListModule,
    AucBidListModule,
    AucEntryPointModule,
    AucFooterModule,
    AucHomeModule,
    AucRootAuthnModule,
    AucRootUnAuthnModule,
    AucRoutingModule,
    AucServiceModule,
    AucWonListModule,
    BrowserModule,
    MfDayJsDateModule,
    MfFillRouterOutletModule,
    MfPortalsModule,
    NgxGoogleAnalyticsModule.forRoot(AUC_ENVIRONMENT.googleAnalyticsTrackingCode),
    NgxGoogleAnalyticsRouterModule,
    OAuthModule.forRoot(),
  ],
  providers: [
    { provide: AF_CONFIG_TOKEN, useValue: AUC_AF_CONFIG },
    { provide: CSP_NONCE, useValue: nonce, },
    { provide: AUC_CONFIG_TOKEN, useValue: AUC_DEFAULT_CONFIG },
    { provide: AUC_ICONS_CONFIG_TOKEN, useValue: AUC_ICONS_DEFAULT_CONFIG },
    { provide: AUC_LINKS_CONFIG_TOKEN, useValue: AUC_LINKS_DEFAULT_CONFIG },
    { provide: HTTP_INTERCEPTORS, useClass: MfAuthAccessTokenInjectorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MfAuthXsrfTokenInjectorInterceptor, multi: true },
    { provide: MF_APP_CONFIG_TOKEN, useValue: AUC_APP_CONFIG },
    { provide: MF_AUTH_CONFIG_TOKEN, useValue: AUC_AUTH_CODE_FLOW_CONFIG },
    { provide: MF_AUTH_LANDING_CONFIG_TOKEN, useValue: AUC_AUTH_LANDING_CONFIG },
    { provide: MF_AUTHENTICATED_URLS_EXCLUDE_TOKEN, useValue: AUC_ENVIRONMENT.authenticatedUrlsExclude },
    { provide: MF_AUTHENTICATED_URLS_TOKEN, useValue: AUC_ENVIRONMENT.authenticatedUrls },
    { provide: MF_FILTER_CONFIG_TOKEN, useValue: MF_PORTALS_FILTER_CONFIG },
    { provide: MF_FILTER_CONVERTER_SERVICE_TOKEN, useClass: MfPortalsFilterConverterService },
    { provide: MF_INPUT_CONFIG_TOKEN, useValue: AUC_INPUT_DEFAULT_CONFIG },
    { provide: MF_LOADING_CONFIG_TOKEN, useValue: AUC_LOADING_CONFIG },
    { provide: MF_MODEL_CONFIGS_TOKEN, useValue: AUC_MODEL_CONFIG },
    { provide: MF_MODEL_VALUE_OUTPUT_FORMATTERS_CONFIG_DEFAULT_TOKEN, useValue: AUC_MODEL_VALUE_OUTPUT_FORMATTERS_CONFIG_DEFAULT },
    { provide: MF_PORTALS_CONFIG_TOKEN, useValue: AUC_PORTALS_DEFAULT_CONFIG },
    { provide: MF_TABLE_CONFIG_TOKEN, useValue: AUC_TABLE_CONFIG },
    { provide: MF_VIEW_MANAGER_CONFIG_TOKEN, useValue: AUC_VIEW_MANAGER_CONFIG },
    { provide: MfViewManagerStorageService, useClass: MfViewManagerSessionStorageService },
    { provide: NGX_MAT_DATE_FORMATS, useValue: AUC_DATE_FORMATS },
    { provide: OAuthStorage, useValue: localStorage },
  ],
  bootstrap: [
    AucEntryPointComponent
  ]
})
export class AucModule {
  public constructor(
    protected _authService: MfAuthService,
    @Inject(MF_AUTH_CONFIG_TOKEN)
    protected _authConfig: AuthConfig,
    protected _userService: AucUserService,
    protected _router: Router,
    protected _storageService: MfSessionStorageService,
    protected _title: Title,
    @Inject(AUC_CONFIG_TOKEN)
    protected _config: AucConfig,
  ) {
    this._authService.configure(_authConfig);
    this._authService.tokenValidationHandler = new JwksValidationHandler();

    this._authService.onLoggedOut.subscribe({
      next: () => {
        this._storageService.remove(MF_VIEW_MANAGER_LOCAL_STORAGE_KEY);
      }
    });

    this._title.setTitle(this._config.name);
  }
}
